import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../../core/services/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorCode } from '../../core/models/problem-details';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss'
})
export class ResetPasswordComponent implements OnInit {
  submitting = false;
  submitSuccess = false;
  errorOnSubmit = false;
  tokenExpired = false;
  resetToken?: string;
  breachWarning = false;
  previousPasswordTried?: string;
  errorCodes: string[] = [];
  errorCodesToShow: string[] = [];

  passwordVisibility = {
    password: false,
    confirmPassword: false
  }

  resetForm = new FormGroup({
    password: new FormControl('', [Validators.required, Validators.pattern('^(?=.*?[A-Z])(?=.*?[0-9]).{8,50}$')]),
    confirmPassword: new FormControl('', Validators.required)
  });

  constructor(private authService: AuthService, private route: ActivatedRoute) {
    // Clear errorOnSubmit when the form is edited
  this.resetForm.valueChanges.subscribe(() => {
      if (this.errorOnSubmit) {
        this.errorOnSubmit = false;
        this.errorCodes = [];
        this.errorCodesToShow = [];
      }
    });
  }

  ngOnInit(): void {
    this.resetToken = this.route.snapshot.queryParams['resetToken'];

    if (this.resetToken != null) {
      this.submitting = true;
      this.authService.verifyResetToken(this.resetToken).subscribe({
        next: () => {
          this.submitting = false;
        },
        error: () => {
          this.submitting = false;
          this.tokenExpired = true;
          this.resetForm.disable();
        }
      })
    }
    else {
      this.tokenExpired = true;
    }
  }

  togglePasswordVisibility(id: 'password' | 'confirmPassword') {
    this.passwordVisibility[id] = !this.passwordVisibility[id];
  }

  reset() {
    if (this.resetForm.invalid) {
      return;
    }

    const password = this.resetForm.value.password;
    const confirmPassword = this.resetForm.value.confirmPassword;

    if (this.resetToken == null || password == null || confirmPassword == null) {
      return;
    }

    this.errorCodes = [];
    
    if (password !== confirmPassword) {
      this.errorOnSubmit = true;
      this.errorCodesToShow = ['PasswordMismatch'];
      this.errorCodes = ['PasswordMismatch'];
      return;
    }

    const ignoreWarnings = this.breachWarning && this.previousPasswordTried === password;
    this.previousPasswordTried = password;

    this.submitting = true;
    this.submitSuccess = false;
    this.errorOnSubmit = false;

    this.authService.resetPassword(this.resetToken, password, ignoreWarnings).subscribe({
      next: () => {
        this.submitting = false;
        this.submitSuccess = true;

        this.resetForm.setValue({ confirmPassword: '', password: '' });
        this.resetForm.markAsPristine();
        this.resetForm.markAsUntouched();
      },
      error: (res: HttpErrorResponse) => {
        const errorCodes = res.error?.errorCodes;
        if (errorCodes != null) {
          this.errorCodes = errorCodes.map((ec: ErrorCode) => ec.code);
          this.errorCodesToShow = this.errorCodes.filter(ec => ec !== 'PasswordMatchesCurrent' && ec !== 'PasswordRequirements');
        }

        this.submitting = false;
        this.errorOnSubmit = true;
 
        if (this.errorCodes.find(ec => ec === 'PasswordBreachWarning')) {
          this.breachWarning = true;
        }
      }
    });
  }
}
